import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  line, ANTHROPIC_SETUP_ROUTE, ASK_CLAUDE_SEAM_OFF, Color, Size, askClaudeCountLine, askClaudeOpenCount, askClaudeQuestionLabel, dealAskClaudeAnswerPath, dealAskClaudeAnswersPath, dealAskClaudePath,
  dealMakeReportPath, discoveryText, formatDateTime, t, type AskClaudeOutcome, type AskClaudeQuestionView, type DiscoveryReportView, type MakeReportOutcome,
} from '@bms2/shared';
import { Button, Textarea } from '../../../components/controls';
import { Modal } from '../../../components/modals/Modal';
import { useDateFormat, useViewerZone } from '../../../shell/ViewerZone';
import { FILES_PATH, del, post } from '../api';
import styles from './AskClaude.module.css';

export interface AskClaudeProps {
  dealId: string;
  questions: readonly AskClaudeQuestionView[];
  writes: boolean;
  /** 🔒 Decision 6004: whether the viewer's role carries Ask Claude; Ask Claude, Resubmit and Make Report are drawn only where it does. */
  asksClaude?: boolean;
  /** 🔒 Decision 6003: the Deal's proposal report, its newest version, or null before the first. */
  report?: DiscoveryReportView | null;
  apiBaseUrl: string;
  onChanged: () => void;
  onError: (message: string) => void;
  onNotice?: (message: string) => void;
}

/** Whether a refusal's body names the seam switched off, the one refusal that offers Settings. */
function seamOff(body: unknown): boolean {
  return body !== null && typeof body === 'object' && (body as { reason?: unknown }).reason === ASK_CLAUDE_SEAM_OFF;
}

/**
 * 🔒 Ask Claude on the Discovery card (piece 19; decision 5530; row T2780 batch 1). The press sends the Deal Pack
 * without its Budget pair through the AI seam, and the open questions Claude answers with come back as a numbered
 * list, each with an answer box that writes its tagged note when it is left. Resubmit sends the answers typed and
 * asks again; Clear Answer takes one answer off and Dismiss Questions the whole list, by the owner's
 * rule of 2026-10-18 that whatever a person can add a person can remove.
 *
 * 🔨 The act is never silent (decision 0559): with the seam off it says so in the application's words and links to
 * Settings › Integrations › Anthropic. 🔨 A question is outside data, shown as plain text and never as markup
 * (decision 0202). A viewer who does not write reads the questions and their answers and changes nothing.
 *
 * 🔒 Make Report (decision 6003) sends the pack with the answers and files the report on the Deal as its Proposal
 * Report, a second press its next version; questions still open ask a confirm first. 🔒 Ask Claude, Resubmit and
 * Make Report are drawn only to a role carrying the permission (decision 6004); the answers stay on the Deal's write.
 */
export function AskClaude({ dealId, questions, writes, asksClaude = false, report = null, apiBaseUrl, onChanged, onError, onNotice }: AskClaudeProps) {
  const [asking, setAsking] = useState(false);
  const [making, setMaking] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const open = askClaudeOpenCount(questions);
  const [offSentence, setOffSentence] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Readonly<Record<string, string>>>({});

  const ask = async () => {
    setAsking(true);
    setOffSentence(null);
    // The boxes typed and not yet left travel with the press, so Resubmit never drops an answer.
    const answers = questions.flatMap((question) => {
      const draft = drafts[question.id];
      return draft !== undefined && draft.trim() !== '' && draft.trim() !== (question.answer ?? '').trim() ? [{ questionId: question.id, value: draft.trim() }] : [];
    });
    const outcome = await post<AskClaudeOutcome>(dealAskClaudePath(dealId), { answers }, apiBaseUrl);
    setAsking(false);
    if (!outcome.ok) {
      if (seamOff(outcome.body)) return setOffSentence(outcome.message);
      return onError(outcome.message);
    }
    setDrafts({});
    onNotice?.(t(outcome.data.asked === 0 ? 'askClaude.none' : 'askClaude.askedSome'));
    onChanged();
  };
  /** 🔒 Make Report (decision 6003): the questions still open ask a confirm first, and the report is filed on the Deal. */
  const make = async () => {
    setConfirming(false);
    setMaking(true);
    setOffSentence(null);
    const outcome = await post<MakeReportOutcome>(dealMakeReportPath(dealId), { withOpenQuestions: open > 0 }, apiBaseUrl);
    setMaking(false);
    if (!outcome.ok) {
      if (seamOff(outcome.body)) return setOffSentence(outcome.message);
      return onError(outcome.message);
    }
    onNotice?.(t('makeReport.made'));
    onChanged();
  };
  const answer = async (question: AskClaudeQuestionView, value: string) => {
    const outcome = await post(dealAskClaudeAnswersPath(dealId), { questionId: question.id, value }, apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    onChanged();
  };
  const clear = async (question: AskClaudeQuestionView) => {
    const outcome = await del(dealAskClaudeAnswerPath(dealId, question.id), apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    setDrafts((held) => ({ ...held, [question.id]: '' }));
    onChanged();
  };
  const dismiss = async () => {
    const outcome = await del(dealAskClaudePath(dealId), apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    setDrafts({});
    onChanged();
  };

  const asks = (
    <Button
      size={Size.Small}
      color={Color.Navy}
      text={[asking ? t('askClaude.asking') : t(questions.length === 0 ? 'askClaude.ask' : 'askClaude.resubmit')]}
      actions={[() => void ask()]}
      disabled={asking}
      disabledReason={t('askClaude.asking')}
    />
  );
  const makes = (
    <span data-make-report="true">
      <Button
        size={Size.Small}
        color={Color.Navy}
        text={[making ? t('makeReport.making') : t('makeReport.make')]}
        actions={[() => (open > 0 ? setConfirming(true) : void make())]}
        disabled={making || asking}
        disabledReason={t(making ? 'makeReport.making' : 'askClaude.asking')}
      />
    </span>
  );
  const presses = asksClaude ? <div className={styles.acts}>{asks}{makes}</div> : null;
  // 🔒 Decision 6003: the report Make Report filed, named beside the press and opened from here, its newest version.
  const reportLine = report ? (
    <p className={styles.hint} data-make-report-file={report.fileId}>
      <a className={styles.link} href={`${apiBaseUrl}${FILES_PATH}/${report.fileId}/stream`} target="_blank" rel="noreferrer">{report.filename}</a>
      {' '}{line('·', t('makeReport.version'), report.version, '·', t('makeReport.madeAt'), formatDateTime(report.madeAt, zone, dateFormat))}
    </p>
  ) : null;
  const confirm = confirming ? (
    <Modal
      open
      title={t('makeReport.openTitle')}
      onCancel={() => setConfirming(false)}
      actions={[{ kind: 'primary', label: t('makeReport.openConfirm'), onAct: make }, { kind: 'cancel', label: t('modal.cancel'), onAct: () => setConfirming(false) }]}
      dataModal="make-report-open"
    >
      <p className={styles.hint}>{discoveryText('makeReport.openBody', { count: open })}</p>
    </Modal>
  ) : null;
  const off = offSentence ? (
    <p className={styles.off} role="alert" data-ask-claude-off="true">
      {offSentence} <Link to={ANTHROPIC_SETUP_ROUTE} className={styles.link}>{t('askClaude.openSettings')}</Link>
    </p>
  ) : null;

  if (questions.length === 0) {
    return presses || reportLine ? <div className={styles.lone} data-ask-claude="none">{presses}{reportLine}{off}{confirm}</div> : null;
  }
  return (
    <section className={styles.list} data-ask-claude="questions" aria-label={t('askClaude.title')}>
      <header className={styles.head}>
        <span className={styles.title}>{t('askClaude.title')}</span>
        <span className={styles.hint} data-ask-claude-counts="true">{askClaudeCountLine(questions)}</span>
      </header>
      {writes ? <p className={styles.hint}>{t('askClaude.hint')}</p> : null}
      <ol className={styles.questions}>
        {questions.map((question, index) => (
          <li key={question.id} className={styles.question} data-ask-claude-question={question.id}>
            {writes ? (
              <>
                <Textarea
                  size={Size.Large}
                  label={askClaudeQuestionLabel(index, question.question)}
                  value={drafts[question.id] ?? question.answer ?? ''}
                  onChange={(value) => setDrafts((held) => ({ ...held, [question.id]: value }))}
                  onBlur={() => {
                    const value = (drafts[question.id] ?? '').trim();
                    if (value !== '' && value !== (question.answer ?? '').trim()) void answer(question, value);
                  }}
                  placeholder={t('discovery.answerPlaceholder')}
                  rows={2}
                  name={`ask-claude-${question.id}`}
                />
                {question.answer !== null ? (
                  <div className={styles.clear}>
                    <Button size={Size.Small} color={Color.Inactive} text={[t('askClaude.clearAnswer')]} actions={[() => void clear(question)]} />
                  </div>
                ) : null}
              </>
            ) : (
              <div className={styles.read}>
                <span className={styles.label}>{askClaudeQuestionLabel(index, question.question)}</span>
                <span className={question.answer ? styles.answer : styles.hint}>{question.answer ?? t('askClaude.notAnswered')}</span>
              </div>
            )}
          </li>
        ))}
      </ol>
      {writes ? (
        <div className={styles.acts}>
          {asksClaude ? asks : null}
          {asksClaude ? makes : null}
          <Button
            size={Size.Small}
            color={Color.Destructive}
            confirm={{ title: t('askClaude.dismissTitle'), consequence: { body: t('askClaude.dismissBody') }, confirmLabel: t('askClaude.dismissConfirm') }}
            text={[t('askClaude.dismiss')]}
            actions={[() => void dismiss()]}
          />
        </div>
      ) : null}
      {reportLine}
      {off}
      {confirm}
    </section>
  );
}
